import React, { useState } from 'react';
import { dayjs } from '@umanforge/common';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RecurringIcon, RootState, ThemeIcon } from '@umanforge/common';
import { EventService } from 'services/events.service';
import { EventData } from 'interfaces/eventData.interface';
import InputText from '@common_components/inputs/textInput.component';
import { EyeIcon } from '@common_components/icons/eye.icon';
import Pagination from '@common_components/inputs/selects/pagination.component';

interface ExtendedEventData extends EventData {
  themeName?: string;
  authorFullname?: string;
  createdDate?: string;
}

interface PaginatedEventResponse {
  events: ExtendedEventData[];
  total: number;
  currentPage: number;
  totalPages: number;
  message?: string;
}

interface EventSearchContainerProps {
  eventManagerId?: string;
  onEventSelect?: ( ( id: string, title: string ) => void ) | ( ( id: string ) => void );
  onEventLinkClick?: ( id: string ) => void;
  onClose?: () => void;
  currentEventId?: string;
}

const EventSearchContainer: React.FC<EventSearchContainerProps> = ( {
  eventManagerId,
  onEventSelect,
  onEventLinkClick,
  onClose,
  currentEventId
} ) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );

  const [ searchFilter, setSearchFilter ] = useState<string>( '' );
  const [ searchResults, setSearchResults ] = useState<ExtendedEventData[]>( [] );
  const [ isLoadingEvents, setIsLoadingEvents ] = useState<boolean>( false );
  const [ hasSearched, setHasSearched ] = useState<boolean>( false );
  const [ pageActual, setPageActual ] = useState<number>( 1 );
  const [ totalPages, setTotalPages ] = useState<number>( 0 );
  const [ totalResults, setTotalResults ] = useState<number>( 0 );
  const [ itemsPerPage ] = useState<number>( 4 );

  const searchEvents = async ( page: number = 1 ) => {
    if ( !eventManagerId || !token || !searchFilter.trim() ) {return;}
    setIsLoadingEvents( true );
    setHasSearched( true );
    try {
      const response = await EventService.getAllEventsByEventManager(
        eventManagerId,
        { page, itemsPerPage, searchFilter: searchFilter.trim() },
        token,
        dispatch
      );

      if ( response && typeof response === 'object' && 'events' in response ) {
        const paginatedResponse = response as unknown as PaginatedEventResponse;
        setSearchResults( paginatedResponse.events || [] );
        setTotalResults( paginatedResponse.total || 0 );
        setTotalPages( paginatedResponse.totalPages || 0 );
        setPageActual( paginatedResponse.currentPage || page );
      } else {
        const events = response as ExtendedEventData[] || [];
        setSearchResults( events );
        setTotalResults( events.length );
        setTotalPages( 1 );
        setPageActual( 1 );
      }
    } catch ( error ) {
      console.error( 'Erreur lors de la recherche des événements:', error );
      setSearchResults( [] );
      setTotalResults( 0 );
      setTotalPages( 0 );
      setPageActual( 1 );
    } finally {
      setIsLoadingEvents( false );
    }
  };

  const handleSearchClick = () => {
    setPageActual( 1 );
    searchEvents( 1 );
  };

  const handlePageChangeCallback = ( page: number ) => {
    if ( page >= 1 && page <= totalPages && !isLoadingEvents ) {
      setPageActual( page );
      searchEvents( page );
    }
  };

  const handleEventClick = ( eventData: ExtendedEventData ) => {
    if ( onEventSelect ) {
      if ( onEventSelect.length === 1 ) {
        ( onEventSelect as ( id: string ) => void )( eventData.event.id || '' );
      } else {
        ( onEventSelect as ( id: string, title: string ) => void )( eventData.event.id || '', eventData.event.title || '' );
      }
    }
    if ( onClose ) {
      onClose();
    }
  };

  return (
    <div className='bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 shadow-lg overflow-hidden min-h-[450px] max-h-[65vh] flex flex-col'>
      {/* Header */}
      <div className='bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-3 text-white flex-shrink-0'>
        <div className='flex items-center gap-3'>
          <div className='bg-white/20 p-1.5 rounded-lg'>
            <EyeIcon size={5} />
          </div>
          <div className='flex-1'>
            <h2 className='text-lg font-semibold'>{t( 'translation:search_events_description' )}</h2>
          </div>
          <div className='flex items-center gap-2'>
            <div className='bg-white/10 px-2 py-1 rounded-full text-sm font-medium'>
              {hasSearched ? (
                <span className='text-yellow-200'>
                  {searchResults.length} {t( 'translation:results' )}
                  {totalResults > searchResults.length && (
                    <span className='text-white/70'> / {totalResults}</span>
                  )}
                </span>
              ) : (
                <span>{t( 'translation:enter_search_term' )}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className='p-5 flex-1 overflow-hidden flex flex-col'>
        {/* Search Input */}
        <div className='mb-4 flex-shrink-0'>
          <div className='relative search-input-container flex gap-2'>
            <div className='flex-1'>
              <InputText
                maxCharacter={ 250 }
                value={searchFilter}
                placeholder={ t( 'common:formalise_your_search' ) }
                onChange={setSearchFilter}
                icon={<EyeIcon size={4} />}
              />
            </div>
            <button
              onClick={handleSearchClick}
              disabled={!searchFilter.trim() || isLoadingEvents}
              className='bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors min-w-[100px]'
            >
              {isLoadingEvents ? (
                <div className='flex items-center justify-center'>
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white'></div>
                </div>
              ) : (
                t( 'common:search' )
              )}
            </button>
          </div>
          <div className='text-sm text-gray-600 mt-2 flex items-center gap-2'>
            <svg className='w-3 h-3 text-blue-500' fill='currentColor' viewBox='0 0 20 20'>
              <path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z' clipRule='evenodd' />
            </svg>
            {t( 'translation:search_help_text_new' )}
          </div>
        </div>

        {/* Results */}
        <div className='flex-1 overflow-y-auto custom-scrollbar'>
          {isLoadingEvents ? (
            <div className='flex flex-col justify-center items-center h-32'>
              <div className='relative'>
                <div className='animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-3'></div>
                <div className='absolute top-0 left-0 right-0 bottom-0 mb-3 flex items-center justify-center'>
                  <EyeIcon size={4} className='text-blue-600 opacity-50' />
                </div>
              </div>
              <div className='text-gray-600 font-medium text-sm'>{t( 'translation:loading' )}...</div>
              <div className='text-gray-500 text-sm mt-1'>{t( 'translation:searching_events' )}</div>
            </div>
          ) : hasSearched && searchResults.length > 0 ? (
            <div className='space-y-3'>
              <div className='grid gap-3 event-search-grid'>
                {searchResults.map( ( eventData, index ) => {
                  const isCurrentEvent = Boolean( currentEventId && eventData.event.id === currentEventId );
                  return (
                    <div
                      key={eventData.event.id || index}
                      className={`bg-white rounded-lg border border-gray-200 transition-all duration-200 overflow-hidden event-card-enter event-card ${
                        isCurrentEvent
                          ? 'opacity-50 cursor-not-allowed bg-gray-50'
                          : 'hover:border-blue-300 hover:shadow-md cursor-pointer group'
                      }`}
                      onClick={() => {
                        if ( !isCurrentEvent ) {
                          return handleEventClick( eventData );
                        }
                      }}
                    >
                      <div className='p-3'>
                        <div className='flex items-start justify-between mb-2'>
                          <div className='flex-1'>
                            <h4 className='font-semibold text-gray-900 text-sm mb-1 group-hover:text-blue-600 transition-colors'>
                              {eventData.event.title}
                            </h4>

                            <div className='flex items-center gap-1 mb-2 flex-wrap'>
                              {eventData.themeName && (
                                <div className='inline-flex items-center gap-1 text-xs text-white px-2 py-0.5 rounded-full font-medium badge-info'>
                                  <ThemeIcon className='w-3 h-3'/>
                                  {eventData.themeName}
                                </div>
                              )}

                              {eventData.event.recurring && (
                                <div className='inline-flex items-center gap-1 text-xs text-white px-2 py-0.5 rounded-full font-medium badge-green'>
                                  <RecurringIcon />
                                  {t( 'translation:recurrent' )}
                                </div>
                              )}

                              {eventData.notes && eventData.notes.length > 0 && (
                                <div className='inline-flex items-center gap-1 text-xs text-white px-2 py-0.5 rounded-full font-medium badge-notes'>
                                  <svg className='w-3 h-3' fill='currentColor' viewBox='0 0 20 20'>
                                    <path d='M9 2a1 1 0 000 2h2a1 1 0 100-2H9z'/>
                                    <path fillRule='evenodd' d='M 5a2 2 0 012-2v1a1 1 0 002 0V3a2 2 0 4012 0v1a1 1 0 002 0V3a2 2 0 012 2v6.586A2 2 0 0115.586 13L7 21.414a2 2 0 01-2.828 0L2.586 20a2 2 0 010-2.828L11 8.586V5a2 2 0 00-2-2H4zm7 7l3 3-3 3-3-3 3-3z' clipRule='evenodd'/>
                                  </svg>
                                  {eventData.notes.length} note{eventData.notes.length > 1 ? 's' : ''}
                                </div>
                              )}
                            </div>
                          </div>

                          {eventData.engagements && eventData.engagements.length > 0 && (
                            <div className='ml-2 text-right'>
                              <div className='text-white px-2 py-1 rounded-lg text-xs font-medium badge-engagement'>
                                {eventData.engagements.length} engagement{eventData.engagements.length > 1 ? 's' : ''}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className='flex items-center justify-between pt-2 border-t border-gray-100'>
                          <div className='text-xs text-gray-500 flex items-center gap-3'>
                            {eventData.authorFullname && (
                              <div className='flex items-center gap-1'>
                                <svg className='w-3 h-3' fill='currentColor' viewBox='0 0 20 20'>
                                  <path fillRule='evenodd' d='M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z' clipRule='evenodd' />
                                </svg>
                                <span className='truncate max-w-[100px]'>{eventData.authorFullname}</span>
                              </div>
                            )}
                            {eventData.createdDate && (
                              <div className='flex items-center gap-1'>
                                <svg className='w-3 h-3' fill='currentColor' viewBox='0 0 20 20'>
                                  <path fillRule='evenodd' d='M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z' clipRule='evenodd' />
                                </svg>
                                {dayjs( eventData.createdDate ).format( 'DD/MM/YYYY' )}
                              </div>
                            )}
                          </div>

                          <div className='flex items-center gap-2'>
                            {onEventLinkClick && eventData.event.id && (
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  onEventLinkClick( eventData.event.id! );
                                }}
                                className='opacity-0 group-hover:opacity-100 transition-opacity text-blue-500 hover:text-blue-700'
                                title={t( 'translation:open_event' )}
                              >
                                <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 20 20'>
                                  <path fillRule='evenodd' d='M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z' clipRule='evenodd' />
                                </svg>
                              </button>
                            )}
                            <div className='opacity-0 group-hover:opacity-100 transition-opacity'>
                              <svg className='w-4 h-4 text-blue-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                } )}
              </div>

              {totalPages > 1 && (
                <div className='mt-4 flex flex-col items-center gap-2'>
                  <Pagination
                    currentPage={pageActual}
                    totalPages={totalPages}
                    onPageChange={handlePageChangeCallback}
                  />
                </div>
              )}

            </div>
          ) : hasSearched ? (
            <div className='text-center py-8'>
              <div className='bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3'>
                <svg className='w-8 h-8 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
                </svg>
              </div>
              <div className='text-lg font-semibold text-gray-900 mb-1'>
                {t( 'translation:no_events_found' )}
              </div>
              <div className='text-gray-600 text-sm max-w-md mx-auto'>
                {t( 'translation:no_events_found_search', { searchTerm: searchFilter } )}
              </div>
            </div>
          ) : (
            <div className='text-center py-8'>
              <div className='bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3'>
                <svg className='w-8 h-8 text-blue-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
                </svg>
              </div>
              <div className='text-lg font-semibold text-gray-900 mb-1'>
                {t( 'translation:ready_to_search' )}
              </div>
              <div className='text-gray-600 text-sm max-w-md mx-auto'>
                {t( 'translation:enter_search_term_description' )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventSearchContainer;
